import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import DashboardLayout from './components/layout/DashboardLayout';
import ProtectedRoute from './routes/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import CreateOrganizationWizard from './pages/admin/CreateOrganizationWizard';
import OrganizationViewPage from './pages/admin/OrganizationViewPage';
import UsersPage from './pages/admin/UsersPage';
import CreateUserPage from './pages/admin/CreateUserPage';
import TeamsPage from './pages/admin/TeamsPage';
import CreateTeamPage from './pages/admin/CreateTeamPage';
import RolesPage from './pages/admin/RolesPage';
import CreateRolePage from './pages/admin/CreateRolePage';
import CalendarPage from './pages/admin/CalendarPage';
import PlaceholderPage from './pages/admin/PlaceholderPage';
import UserLayout from './components/layout/UserLayout';
import EngagementsPage from './pages/user/EngagementsPage';
import CreateEngagementPage from './pages/user/CreateEngagementPage';
import EngagementDetailPage from './pages/user/EngagementDetailPage';
import CreateTaskPage from './pages/user/CreateTaskPage';
import TaskDetailPage from './pages/user/TaskDetailPage';
import SubTaskDetailPage from './pages/user/SubTaskDetailPage';
import DailyDirectionsPage from './pages/user/DailyDirectionsPage';
import CreateDailyDirectionPage from './pages/user/CreateDailyDirectionPage';
import DailyDirectionStatusPage from './pages/user/DailyDirectionStatusPage';
import CitizenRequestsPage from './pages/user/CitizenRequestsPage';
import CreateCitizenRequestPage from './pages/user/CreateCitizenRequestPage';
import { fontFamily } from './theme/tokens';

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily,
          colorPrimary: '#0061ff',
          borderRadius: 8,
          controlHeight: 44,
          colorBorder: '#cbd5e1',
          colorText: '#00173e',
          colorTextPlaceholder: '#9ca3af',
          fontSize: 14,
        },
        components: {
          Select: { optionSelectedBg: '#eef4ff', optionSelectedColor: '#0061ff' },
          DatePicker: { activeBorderColor: '#0061ff' },
        },
      }}
    >
      <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Administrative Flow */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute flow="admin">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="organizations" replace />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/new" element={<CreateOrganizationWizard />} />
          <Route path="organizations/new/:id" element={<CreateOrganizationWizard />} />
          <Route path="organizations/:id" element={<OrganizationViewPage />} />
          <Route path="organizations/:id/edit" element={<CreateOrganizationWizard />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/new" element={<CreateUserPage />} />
          <Route path="users/:id/edit" element={<CreateUserPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="teams/new" element={<CreateTeamPage />} />
          <Route path="teams/:id/edit" element={<CreateTeamPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="roles/new" element={<CreateRolePage />} />
          <Route path="roles/:id/edit" element={<CreateRolePage />} />
          <Route path="calendar" element={<CalendarPage />} />
        </Route>

        {/* User Flow */}
        <Route
          path="/user"
          element={
            <ProtectedRoute flow="user">
              <UserLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="engagements" replace />} />
          <Route path="engagements" element={<EngagementsPage />} />
          <Route path="engagements/new" element={<CreateEngagementPage />} />
          <Route path="engagements/:id/edit" element={<CreateEngagementPage />} />
          <Route path="engagements/:id" element={<EngagementDetailPage />} />
          <Route path="engagements/:id/tasks/new" element={<CreateTaskPage />} />
          <Route path="engagements/:id/tasks/:taskId/edit" element={<CreateTaskPage />} />
          <Route path="engagements/:id/tasks/:taskId" element={<TaskDetailPage />} />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/new"
            element={<CreateTaskPage variant="subtask" />}
          />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/:subId/edit"
            element={<CreateTaskPage variant="subtask" />}
          />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/:subId"
            element={<SubTaskDetailPage />}
          />
          <Route
            path="projects"
            element={<PlaceholderPage title="Projects" description="Track and manage your projects." />}
          />
          <Route path="directions" element={<DailyDirectionsPage />} />
          <Route path="directions/new" element={<CreateDailyDirectionPage />} />
          <Route path="directions/:id/edit" element={<CreateDailyDirectionPage />} />
          <Route path="directions/:id" element={<DailyDirectionStatusPage />} />
          <Route
            path="invitations"
            element={
              <PlaceholderPage title="Invitations" description="Manage event and meeting invitations." />
            }
          />
          <Route path="citizen-requests" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/new" element={<CreateCitizenRequestPage />} />
          <Route path="citizen-requests/wedding" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/shoksabha" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/events" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/media" element={<CitizenRequestsPage />} />
          <Route
            path="progress"
            element={
              <PlaceholderPage title="Progress Dashboard" description="Monitor campaign progress and KPIs." />
            }
          />
          <Route
            path="reports"
            element={<PlaceholderPage title="Reports" description="Generate and view field reports." />}
          />
          <Route
            path="learning"
            element={
              <PlaceholderPage title="Learning Center" description="Training material and resources." />
            }
          />
        </Route>

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
